import { fromJS, Map, List } from 'immutable'
import { prop, last, propEq, cond, T, always, isNil } from 'ramda'
import * as actions from 'actions/search.js'
import {
  COLLECTIONS,
  SUPPORT_PARAMETER,
  REFERENCE_PARAMETER,
  FEATURE,
  FIRST_DROPDOWN,
  SECOND_DROPDOWN,
  THIRD_DROPDOWN,
  FOURTH_DROPDOWN,
  FIFTH_DROPDOWN,
  TRUCK,
  PTO_TEMPLATES,
  FEATURE_COLLECTIONS,
  VEHICLES,
  FAST_FEATURES,
} from 'utils/constants'
import { FEATURES } from 'utils/constants/vehicle-constants'
import { findMostRecentRevision } from 'utils/revisionHelpers'
import { extractFeatureId } from 'utils/uriHelpers'
import { createReducer } from './utils'

const RESULTS_LIMIT = 10
const SEARCH_START_INDEX = 1
const INITIAL_TOTAL_RESULTS = -1

const initialRPSearchCollectionState = {
  startIndex: SEARCH_START_INDEX,
  searchString: '',
  results: [],
  filters: {},
  totalResults: INITIAL_TOTAL_RESULTS,
  searchUri: '',
  dropdownSelections: {},
}
const initialSPSearchCollectionState = {
  startIndex: SEARCH_START_INDEX,
  searchString: '',
  results: [],
  filters: {},
  filterGroups: {},
  totalResults: INITIAL_TOTAL_RESULTS,
  searchUri: '',
  filterSelections: {
    filterSelection1: '',
    filterSelection2: '',
  },
}
const initialDashboardSearchCollectionState = {
  startIndex: SEARCH_START_INDEX,
  searchString: '',
  results: [],
  filters: {},
  totalResults: INITIAL_TOTAL_RESULTS,
  searchUri: '',
  dropdownSelections: {},
  selectedScheduleDaysOut: '0',
}

const initialFeatureSearchCollectionState = {
  startIndex: SEARCH_START_INDEX,
  searchString: '',
  results: [],
  filters: {},
  chipFilters: {},
  totalResults: INITIAL_TOTAL_RESULTS,
  searchUri: '',
  dropdownSelections: {},
  busFilterFlag: false,
}

const initialFeatureCollectionsSearchState = {
  startIndex: SEARCH_START_INDEX,
  searchString: '',
  results: [],
  filters: {},
  chipFilters: { queries: { collectionTypes: ['Daimler', 'Custom'] } },
  totalResults: INITIAL_TOTAL_RESULTS,
  searchUri: '',
  dropdownSelections: {},
  busFilterFlag: false,
}

const initialPtoTemplateSearchCollectionState = {
  startIndex: SEARCH_START_INDEX,
  searchString: '',
  results: [],
  filters: {},
  chipFilters: {},
  totalResults: INITIAL_TOTAL_RESULTS,
  searchUri: '',
  dropdownSelections: {},
  busFilterFlag: false,
}

const initialFastFeatureSearchCollectionState = {
  startIndex: SEARCH_START_INDEX,
  searchString: '',
  results: [],
  searchUri: '/features/search-fast-features',
  totalResults: INITIAL_TOTAL_RESULTS,
  filters: {},
  chipFilters: { queries: { ecuTypes: ['XMC 3', 'XMC 4'] } },
}

export const initialState = fromJS({
  isSearching: false,
  limit: RESULTS_LIMIT,
  startIndex: SEARCH_START_INDEX,
  searchSubmitted: false,
  collections: [
    COLLECTIONS[SUPPORT_PARAMETER],
    COLLECTIONS[REFERENCE_PARAMETER],
    COLLECTIONS[FEATURE],
    COLLECTIONS[FEATURE_COLLECTIONS],
    COLLECTIONS[FAST_FEATURES],
    COLLECTIONS[TRUCK],
    COLLECTIONS[VEHICLES],
    COLLECTIONS[PTO_TEMPLATES],
  ],
  [COLLECTIONS[SUPPORT_PARAMETER]]: initialSPSearchCollectionState,
  [COLLECTIONS[REFERENCE_PARAMETER]]: initialRPSearchCollectionState,
  [COLLECTIONS[FEATURE]]: initialFeatureSearchCollectionState,
  [COLLECTIONS[FAST_FEATURES]]: initialFastFeatureSearchCollectionState,
  [COLLECTIONS[FEATURE_COLLECTIONS]]: initialFeatureCollectionsSearchState,
  [COLLECTIONS[TRUCK]]: initialDashboardSearchCollectionState,
  [COLLECTIONS[VEHICLES]]: initialDashboardSearchCollectionState,
  [COLLECTIONS[PTO_TEMPLATES]]: initialPtoTemplateSearchCollectionState,
})

const handlers = {
  [actions.ADS_START_SEARCH]: startSearch,
  [actions.ADS_SUBMIT_SEARCH_START]: setIsSearchSubmitted,
  [actions.ADS_SET_FILTERS]: setFilters,
  [actions.ADS_SET_CHIP_FILTERS]: setChipFilters,
  [actions.ADS_RESET_CHIP_FILTERS]: resetChipFilters,
  [actions.ADS_SET_FILTER_DATA_BY_COLLECTION]: setFilterDataByCollection,
  [actions.ADS_GET_FILTER_DATA_BY_COLLECTION_FAILURE]: setFilterDataError,
  [actions.ADS_SET_FILTER_TERMS]: setFilterTerms,
  [actions.ADS_SET_FILTER_SELECTION]: setFilterSelection,
  [actions.ADS_SET_FILTER_TERM]: setFilterTerm,
  [actions.ADS_SET_FILTER_TERM_FROM_DASH]: setFilterTermFromDash,
  [actions.ADS_SET_FILTER_TERM_FROM_ECU]: setFilterTermFromEcu,
  [actions.ADS_SET_FAST_FEATURES_FILTERS_FROM_ECU_DASH]:
    setFastFeaturesFiltersFromEcuAndDash,
  [actions.ADS_REMOVE_FILTER_TERM]: removeFilterTerm,
  [actions.ADS_CLEAR_FILTER_TERMS]: clearFilterTerms,
  [actions.ADS_SET_DROPDOWN_SELECTION_BY_KEY]: setDropdownSelectionByKey,
  [actions.ADS_CLEAR_DROPDOWN_BY_KEY]: clearDropdownByKey,
  [actions.ADS_CLEAR_ALL_DROPDOWNS]: clearAllDropdowns,
  [actions.ADS_CLEAR_SEARCH_STRING]: clearSearchString,
  [actions.ADS_SET_SEARCH_STRING]: setSearchString,
  [actions.ADS_SET_SEARCH_RESULTS]: setSearchResults,
  [actions.ADS_FETCH_SEARCH_RESULTS_FAILURE]: clearIsSearching,
  [actions.ADS_SET_IS_SEARCHING]: setIsSearching,
  [actions.ADS_SET_COLLECTION_STARTINDEX]: setCollectionStartIndex,
  [actions.ADS_RESET_COLLECTION_STARTINDEX]: resetCollectionStartIndex,
  [actions.ADS_RESET_COLLECTION_SEARCH_RESULTS_AND_INITIAL_STATE]:
    resetCollectionSearchResultsAndInitialState,
  [actions.ADS_UPDATE_SEARCH_RESULT_ITEM]: updateSearchResultItem,
  [actions.ADS_SET_SELECTED_DASH_BUILDER_DAYS_OUT]:
    setProductionScheduleDaysOut,
  [actions.ADS_RESET_DASH_BUILDER_SELECTED_DAYS_OUT]:
    resetProductionScheduleDaysOut,
  [actions.ADS_SET_TOTAL_DASH_BUILDER_PRODUCTION_SCHEDULE_RECORDS]:
    setTotalProductionScheduleRecords,
  [actions.ADS_RESET_ALL_SEARCH]: resetAllSearch,
  [actions.GET_SEARCH_SUCCESS]: setSearchResults,
  [actions.GET_SEARCH_REQUEST]: setIsSearching,
  [actions.ADS_SET_BUS_FILTER_FLAG]: setBusFilter,
}

const getSearchByCollection = (state, collectionName) =>
  state.get(collectionName)

function startSearch(
  state,
  { collectionName, searchUri, addToSearchRequestBody }
) {
  const search = getSearchByCollection(state, collectionName)
  const newResourceState = search.merge(
    fromJS({
      searchUri,
      addToSearchRequestBody,
    })
  )
  return state.set(collectionName, newResourceState)
}

function setFilters(state, { collectionName, filters }) {
  const search = getSearchByCollection(state, collectionName)
  const newResourceState = search.merge(
    fromJS({
      filters,
    })
  )
  return state.set(collectionName, newResourceState)
}

function setIsSearchSubmitted(state, { start, collectionName }) {
  return state
    .set('searchSubmitted', start)
    .setIn([collectionName, 'results'], List())
}

function setChipFilters(state, { collectionName, chipFilters }) {
  const search = getSearchByCollection(state, collectionName)
  const newResourceState = search.merge(
    fromJS({
      chipFilters,
    })
  )
  return state.set(collectionName, newResourceState)
}

function resetChipFilters(state, { collectionName, chipFilters }) {
  const search = getSearchByCollection(state, collectionName)
  const newResourceState = search.merge(
    fromJS({
      chipFilters,
    })
  )
  return state.set(collectionName, newResourceState)
}

const getRPOrFeatureSearchData = (filtersData) => {
  const filters = prop('filterGroups', filtersData)
  if (!filters) return fromJS({})
  const firstDropdownData = filters.filter((filterObj) =>
    isNil(prop('group', filterObj))
  )
  const allIds = [...new Set(filters.map((data) => prop('id', data)))]
  const allGroups = [...new Set(filters.map((data) => prop('group', data)))]
  const allGroupIds = allIds.filter((id) => allGroups.includes(id))
  const dropdownDataByGroup = allGroupIds.reduce((acc, currGroup) => {
    const formattedFilterData = filters.filter((filterObj) =>
      allGroupIds.includes(prop('group', filterObj))
    )
    const listByGroup = formattedFilterData.filter(
      (dataObj) => dataObj.group === currGroup
    )
    if (isNil(listByGroup)) return acc
    return {
      ...acc,
      [currGroup]: listByGroup,
    }
  }, {})
  const newResourceState = fromJS({
    firstFilterCriteria: firstDropdownData,
    advancedFilterGroups: dropdownDataByGroup,
  })
  return newResourceState
}

const getDashboardSearchData = (filtersData) => {
  const filters = prop('filterGroups', filtersData)
  if (!filters) return fromJS({})
  const firstDropdownData = filters.filter((filterObj) =>
    isNil(prop('group', filterObj))
  )
  const allIds = [...new Set(filters.map((data) => prop('id', data)))]
  const allGroups = [...new Set(filters.map((data) => prop('group', data)))]
  const allGroupIds = allIds.filter((id) => allGroups.includes(id))
  const dropdownDataByGroup = allGroupIds.reduce((acc, currGroup) => {
    const formattedFilterData = filters.filter((filterObj) =>
      allGroupIds.includes(prop('group', filterObj))
    )
    const listByGroup = formattedFilterData.filter(
      (dataObj) => dataObj.group === currGroup
    )
    if (isNil(listByGroup)) return acc
    return {
      ...acc,
      [currGroup]: listByGroup,
    }
  }, {})
  const newResourceState = fromJS({
    firstFilterCriteria: firstDropdownData,
    advancedFilterGroups: dropdownDataByGroup,
  })
  return newResourceState
}
const getSPSearchData = (filterData) => {
  const filterGroups = prop('filterGroups', filterData)
  if (!filterGroups) return fromJS({})
  const groupOrder = filterGroups.map((group) => prop('groupName', group))
  const formattedGroups = filterGroups.reduce((acc, curr) => {
    const { descriptions, filters } = curr
    const types = Object.entries(descriptions).reduce(
      (descAcc, [key, value]) => {
        const newKey = last(value.split('/')).trim().toLowerCase()
        return {
          ...descAcc,
          [newKey]: key,
        }
      },
      {}
    )
    const filtersByValue = filters.reduce(
      (filterAcc, currentFilter) => ({
        ...filterAcc,
        [prop('value', currentFilter)]: currentFilter,
      }),
      {}
    )
    return {
      ...acc,
      [prop('groupName', curr)]: {
        ...curr,
        filters: filtersByValue,
        types,
      },
    }
  }, {})

  formattedGroups.groupOrder = groupOrder
  const newResourceState = fromJS({
    filterGroups: formattedGroups,
  })
  return newResourceState
}

const getCollectionNameEq = propEq('collectionName')
const getSearchDataByCollection = cond([
  [getCollectionNameEq(REFERENCE_PARAMETER), getRPOrFeatureSearchData],
  [getCollectionNameEq(SUPPORT_PARAMETER), getSPSearchData],
  [getCollectionNameEq(FEATURE), getRPOrFeatureSearchData],
  [getCollectionNameEq(TRUCK), getDashboardSearchData],
  [getCollectionNameEq(VEHICLES), getDashboardSearchData],
  [T, always(null)],
])

function setFilterDataError(
  state,
  { actionArguments: { collectionName } = {} }
) {
  if (!collectionName) return state
  const search = getSearchByCollection(state, collectionName)
  if (!search) return state
  const newResourceState = search.set('filterError', true)
  return state.set(collectionName, newResourceState)
}

function setFilterDataByCollection(
  state,
  { collectionName, data: { filterGroups } }
) {
  const search = getSearchByCollection(state, collectionName)
  const newResourceStateByCollection = getSearchDataByCollection({
    collectionName,
    filterGroups,
  })
  const newResourceState = search.merge(newResourceStateByCollection)
  return state.set(collectionName, newResourceState)
}

function setFilterSelection(state, { collectionName, filterKey, value }) {
  const search = getSearchByCollection(state, collectionName)
  const newResourceState = search.mergeIn(
    ['filterSelections', filterKey],
    value
  )
  return state.set(collectionName, newResourceState)
}

const getDropdownKeyEq = propEq('dropdownKey')
const getFirstDropdownData = (selection) => {
  const { selectedValue } = selection
  const [id, title] = selectedValue.split('--')
  const data = {
    title,
    id,
    selectedValue,
  }
  return data
}

const getSecondDropdownData = (selection) => {
  const { selectedValue } = selection
  const [id, schemaId, value, groupName] = selectedValue.split('--')
  const data = {
    id,
    schemaId,
    value,
    groupName,
    selectedValue,
  }
  return data
}

const getThirdDropdownData = (selection) => {
  const { selectedValue } = selection
  if (propEq('collectionName', FEATURE, selection)) {
    const [id, value, label, groupName] = selectedValue.split('--')
    const data = {
      id,
      value,
      label,
      groupName,
      selectedValue,
    }
    return data
  }
  const [id, schemaId, value, groupName] = selectedValue.split('--')
  const data = {
    id,
    schemaId,
    value,
    groupName,
    selectedValue,
  }
  return data
}

const getFourthDropdownData = (selection) => {
  const { selectedValue } = selection
  if (propEq('collectionName', FEATURE, selection)) {
    const [id, value, groupName] = selectedValue.split('--')
    const data = {
      id,
      value,
      groupName,
      selectedValue,
    }
    return data
  }
  const [id, value, groupName] = selectedValue.split('--')
  const data = {
    id,
    value,
    groupName,
    selectedValue,
  }
  return data
}

const getFifthDropdownData = (selection) => {
  const { selectedValue } = selection
  const [id, label, schemaId, value, groupName] = selectedValue.split('--')
  const data = {
    id,
    label,
    schemaId,
    value,
    groupName,
    selectedValue,
  }
  return data
}

const getSelectionDataByDropdown = cond([
  [getDropdownKeyEq(FIRST_DROPDOWN), getFirstDropdownData],
  [getDropdownKeyEq(SECOND_DROPDOWN), getSecondDropdownData],
  [getDropdownKeyEq(THIRD_DROPDOWN), getThirdDropdownData],
  [getDropdownKeyEq(FOURTH_DROPDOWN), getFourthDropdownData],
  [getDropdownKeyEq(FIFTH_DROPDOWN), getFifthDropdownData],
  [getCollectionNameEq(TRUCK), getSecondDropdownData],
  [getCollectionNameEq(VEHICLES), getSecondDropdownData],
  [T, always(null)],
])

function setDropdownSelectionByKey(
  state,
  { selectedValue, collectionName, dropdownKey }
) {
  // const transform = getSelectionTransformByDropdown[dropdownKey]
  // console.log(transform, 'transform')
  // const data = transformDropdownData(transform)({ selectedValue, dropdownKey })
  const data = getSelectionDataByDropdown({
    dropdownKey,
    selectedValue,
    collectionName,
  })
  return state.setIn(
    [collectionName, 'dropdownSelections', dropdownKey],
    fromJS(data)
  )
}

function setFilterTerms(state, { collectionName, filters }) {
  return state.setIn([collectionName, 'filters'], fromJS(filters))
}

function setFilterTerm(state, { collectionName, filterTerm }) {
  const { value } = filterTerm
  return state.setIn([collectionName, 'filters', value], fromJS(filterTerm))
}

function setFilterTermFromDash(state, { filterTerm }) {
  const { schema, value, filterType, switches } = filterTerm
  const filterTermData = {
    schema,
    value,
    filterType,
    switches,
  }
  return state.setIn([FEATURE, 'filters', value], fromJS(filterTermData))
}

function setFilterTermFromEcu(state, { filterTerm }) {
  const { value } = filterTerm
  return state.setIn([FEATURE, 'filters', value], fromJS(filterTerm))
}

function setFastFeaturesFiltersFromEcuAndDash(state, { filters }) {
  return state.setIn([FAST_FEATURES, 'filters'], fromJS(filters))
}

function removeFilterTerm(state, { collectionName, value }) {
  return state.deleteIn([collectionName, 'filters', value])
}

function clearDropdownByKey(state, { collectionName, dropdownKey }) {
  return state.deleteIn([collectionName, 'dropdownSelections', dropdownKey])
}

function clearFilterTerms(state, { collectionName }) {
  const search = getSearchByCollection(state, collectionName)
  const newResourceState = search.merge({ filters: new Map() })
  return state.set(collectionName, newResourceState)
}

function clearAllDropdowns(state, { collectionName }) {
  return state.setIn([collectionName, 'dropdownSelections'], new Map())
}

function clearSearchString(state, { collectionName }) {
  const search = getSearchByCollection(state, collectionName)
  // Reset search state to defaults
  const newResourceState = search.merge({
    // Clear search input
    searchString: '',
    // Empty results list
    results: new List(),
    // Reset total count
    totalResults: INITIAL_TOTAL_RESULTS,
    // Reset paging index
    startIndex: SEARCH_START_INDEX,
  })
  return state.set(collectionName, newResourceState)
}

function resetProductionScheduleDaysOut(state, { collectionName }) {
  return state.setIn([collectionName, 'selectedScheduleDaysOut'], '0')
}

function setSearchString(state, { collectionName, searchString }) {
  const search = getSearchByCollection(state, collectionName)
  const newResourceState = search.merge({
    searchString,
  })
  return state.set(collectionName, newResourceState)
}

function setBusFilter(state, { collectionName, busFilterFlag }) {
  const search = getSearchByCollection(state, collectionName)
  const newResourceState = search.merge({
    busFilterFlag,
  })
  return state.set(collectionName, newResourceState)
}

function setProductionScheduleDaysOut(
  state,
  { collectionName, selectedScheduleDaysOut }
) {
  const search = getSearchByCollection(state, collectionName)
  const newResourceState = search.merge({
    selectedScheduleDaysOut,
  })
  return state.set(collectionName, newResourceState)
}

function setSearchResults(state, action) {
  const { collectionName } = action

  const search = getSearchByCollection(state, collectionName)
  const { data } = action

  // For FEATURE searches, update each feature's revision to most recent
  let processedData = data
  if (collectionName === FEATURE || collectionName === FAST_FEATURES) {
    const featuresKey =
      collectionName !== FAST_FEATURES ? collectionName : FEATURES
    const features = data[featuresKey]

    if (features && Array.isArray(features)) {
      const updatedFeatures = features.map((feature) => {
        // If feature has revisions array with items, find the most recent
        if (feature.revisions && feature.revisions.length > 0) {
          const mostRecentRev = findMostRecentRevision(feature.revisions)
          if (mostRecentRev) {
            // Update all fields to match the most recent revision
            return {
              ...feature,
              id:
                mostRecentRev.id ||
                extractFeatureId(mostRecentRev.uri) ||
                feature.id,
              uri: mostRecentRev.uri || feature.uri,
              revision:
                mostRecentRev.revision ||
                mostRecentRev.release ||
                feature.revision,
              status: mostRecentRev.status || feature.status,
              partNumber: mostRecentRev.partNumber || feature.partNumber,
            }
          }
        }
        return feature
      })

      processedData = {
        ...data,
        [featuresKey]: updatedFeatures,
      }
    }
  }

  const newResourceState = search.merge({
    results: fromJS(
      processedData[
        collectionName !== FAST_FEATURES ? collectionName : FEATURES
      ]
    ),
    totalResults: data.total,
  })
  return state.set(collectionName, newResourceState).set('isSearching', false)
}

function setTotalProductionScheduleRecords(state, action) {
  const { collectionName, total } = action
  const search = getSearchByCollection(state, collectionName)
  const newResourceState = search.merge({
    totalResults: total,
  })
  return state.set(collectionName, newResourceState).set('isSearching', false)
}

function clearIsSearching(state) {
  return state.set('isSearching', false)
}

function setIsSearching(state, { isSearching }) {
  return state.set('isSearching', isSearching)
}

function setCollectionStartIndex(state, { collectionName, startIndex }) {
  const search = getSearchByCollection(state, collectionName)
  const newResourceState = search.merge({
    startIndex,
  })
  return state.set(collectionName, newResourceState)
}

function resetCollectionStartIndex(state, { collectionName }) {
  const search = getSearchByCollection(state, collectionName)
  const newResourceState = search.merge({
    startIndex: SEARCH_START_INDEX,
  })
  return state.set(collectionName, newResourceState)
}

function resetCollectionSearchResultsAndInitialState(
  state,
  { collectionName }
) {
  const search = getSearchByCollection(state, collectionName)
  const newResourceState = search.merge({
    results: new List(),
    totalResults: INITIAL_TOTAL_RESULTS,
    startIndex: SEARCH_START_INDEX,
  })
  return state.set(collectionName, newResourceState)
}

function updateSearchResultItem(
  state,
  { collectionName, itemId, updatedData }
) {
  const search = getSearchByCollection(state, collectionName)
  const results = search.get('results')

  // Find item index by matching id
  const itemIndex = results.findIndex((item) => item.get('id') === itemId)

  if (itemIndex === -1) {
    return state // Item not found, no update
  }

  // Merge updated data into the item
  const updatedResults = results.update(itemIndex, (item) =>
    item.merge(fromJS(updatedData))
  )

  const newResourceState = search.set('results', updatedResults)
  return state.set(collectionName, newResourceState)
}

function resetAllSearch(state, { collectionName }) {
  if (collectionName === FEATURES) {
    // preserve so not to refetch advacned filters from API and keep UI hydrated
    const advancedFilterGroups = state.getIn([FEATURES, 'advancedFilterGroups'])
    const firstFilterCriteria = state.getIn([FEATURES, 'firstFilterCriteria'])
    const chipFilters = state.getIn([FEATURES, 'chipFilters'])
    const addToSearchRequestBody = state.getIn([
      FEATURES,
      'addToSearchRequestBody',
    ])

    return initialState
      .setIn([FEATURES, 'advancedFilterGroups'], advancedFilterGroups)
      .setIn([FEATURES, 'firstFilterCriteria'], firstFilterCriteria)
      .setIn([FEATURES, 'addToSearchRequestBody'], addToSearchRequestBody)
      .setIn([FEATURES, 'chipFilters'], chipFilters)
  }

  return initialState
}

export default createReducer(initialState, handlers)
